import { useCallback, useEffect, useState } from 'react';
import { outlineConfirmPush, wsChannel } from '@xdocs/schemas';
import type { DocumentOutline, JobAccepted, OutlineJsonb, OutlineStatus } from '@xdocs/schemas';
import { requestTyped } from '@xdocs/ws-protocol';
import { useWs } from '../../ws/WsProvider.js';

export interface UseOutlineConfirmResult {
  /** `null` until an `outline-confirm:{documentId}` push has arrived this
   * session — see this task's returned open-issues list: there is no WS op
   * to fetch a document's current outline status/id on cold load, only the
   * live channel, so a page opened directly (not right after `generate.
   * start`) won't know about a pending outline until the next event fires. */
  status: OutlineStatus | null;
  outlineId: string | null;
  confirm: (outlineId: string, edits: OutlineJsonb | undefined) => Promise<JobAccepted>;
  reject: (outlineId: string, reason: string | undefined) => Promise<DocumentOutline>;
}

/**
 * F15 confirm-gate channel wiring. Subscribes `outline-confirm:{documentId}`
 * for the proposal-delivery/confirm/reject relay (§5.5) and exposes the two
 * real ops (`outline.confirm`/`outline.reject`, both in the frozen M1
 * op-contracts). Does NOT fetch the outline's actual chapter content — no
 * `outline.get`-shaped op exists yet (see open issues); `OutlineConfirmPanel`
 * only renders once a caller has content from elsewhere (e.g. the wizard's
 * own in-memory state right after proposing).
 */
export function useOutlineConfirm(documentId: string): UseOutlineConfirmResult {
  const { wsClient } = useWs();
  const [state, setState] = useState<{ status: OutlineStatus; outlineId: string } | null>(null);

  useEffect(() => {
    setState(null);
    const channel = wsChannel.outlineConfirm(documentId);
    return wsClient.subscribe(channel, (envelope) => {
      const parsed = outlineConfirmPush.safeParse(envelope.payload);
      if (parsed.success) setState({ status: parsed.data.status, outlineId: parsed.data.outlineId });
    });
  }, [wsClient, documentId]);

  const confirm = useCallback(
    (outlineId: string, edits: OutlineJsonb | undefined) =>
      requestTyped(wsClient, 'outline.confirm', { documentId, outlineId, edits }),
    [wsClient, documentId],
  );

  const reject = useCallback(
    (outlineId: string, reason: string | undefined) =>
      requestTyped(wsClient, 'outline.reject', { documentId, outlineId, reason }),
    [wsClient, documentId],
  );

  return { status: state?.status ?? null, outlineId: state?.outlineId ?? null, confirm, reject };
}
